<script setup>
import { computed, defineProps, onMounted, ref } from "vue";
import Deposit from "@/models/Deposit";
import { formatMoney } from "@/utils/money";
import dayjs from "dayjs";
import Qrcode from "@/components/ui/Qrcode.vue";
import { on as onEvent } from "@/eventBus";
import CreatePayment from "@/components/payment/CreatePayment.vue";
import PaymentsList from "@/components/payment/PaymentsList.vue";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DepositStatus from "@/components/payment/DepositStatus.vue";
import { Button } from "@/shadcn/components/ui/button/index.js";
import { RefreshCcwIcon, QrCodeIcon, ExternalLinkIcon, CirclePlusIcon } from "@lucide/vue";

const { baseFilters, paymentModes, paymentStatus } = defineProps({
  baseFilters: {
    type: Object,
    default: () => {},
  },
  paymentModes: {
    type: Array,
    default: () => [],
  },
  paymentStatus: {
    type: Array,
    default: () => [],
  }
});

const deposits = ref([]);
const getDeposits = async () => {
  let depositModel = Deposit

  Object.entries(baseFilters).forEach(filter => {
    depositModel = depositModel.where(filter[0], filter[1]);
  });

  const { data:  deposit } = await depositModel.include(['payments', 'payments.paymentProvider']).get();
  return deposit
};

const locale = document.documentElement.lang;

onMounted(async () => {
  deposits.value = await getDeposits();
});

const showQrcode = ref(false);
const qrcodeValue = ref('');
const displayQrcode = (url) => {
  qrcodeValue.value = url;
  showQrcode.value = true;
};
onEvent('deposit.created', async () => {
  deposits.value = await getDeposits();
});

const onClickCancel = async (deposit) => {
  if(!confirm('Are you sure you want to cancel this deposit ?')) {
    return;
  }

  await deposit.delete();
  deposits.value = await getDeposits();
}


const showCreatePayment = ref(false);
const depositToPay = ref(null);
const amountToPay = computed(() => {
  return depositToPay?.value?.amount.amount ? parseInt(depositToPay?.value?.amount.amount) : 0
});
const currency = depositToPay?.value?.amount.currency ?? 'EUR';
const paybleId = computed(() => {
  return depositToPay?.value?.id ?? null
})


const onClickPayDeposit = (deposit) => {
  depositToPay.value = deposit;
  showCreatePayment.value = true;
};
const onCloseCreatePayment = async () => {
  showCreatePayment.value = false;
  depositToPay.value = null;
  deposits.value = await getDeposits();
};
const handlePaymentUpdate = async (payment) => {
  deposits.value = await getDeposits()
};

const requirePayment = (deposit) => {
  return deposit.status === 'pending'
    && deposit?.amount?.amount !== deposit.payments.reduce((acc, payment) => acc + (payment?.paid_amount?.amount ?? 0), 0)
};
const refreshingDeposit = ref(null);
const onClickRefreshDeposit = async (deposit) => {
  console.log('Refresh deposit', deposit);
  refreshingDeposit.value = deposit.id;
  const depositIndex = await deposits.value.findIndex(d => d.id === deposit.id);
  deposits.value[depositIndex] = await deposit.refresh();
  refreshingDeposit.value = null;
};

</script>
<template>
  <div>
    <div v-for="(deposit, i) in deposits" class="card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center">
          <h4 class="mb-0">
            {{ formatMoney(deposit.amount, locale) }}
            <DepositStatus :status="deposit.status" class="ml-2" />
          </h4>
          <Button variant="outline"
                  size="sm"
                  :loading="refreshingDeposit === deposit.id"
                  @click.prevent="onClickRefreshDeposit(deposit)">
            <RefreshCcwIcon class="tw:w-5 tw:h-5" />
            {{ $t('Refresh') }}
          </Button>
        </div>

        <div class="mt-2">
          <strong>{{ $t('Payments') }}:</strong>
          <PaymentsList :payments="deposit.payments"
                        :status="deposit.status"
                        class="mt-2"
                        @payment:updated="handlePaymentUpdate"></PaymentsList>
          <template v-if="requirePayment(deposit)">
            <div class="tw:flex tw:flex-wrap tw:justify-start tw:items-center tw:gap-2 tw:mt-2">
              <Button variant="secondary" as-child>
                <a :href="deposit.url" target="_blank">
                  <ExternalLinkIcon class="tw:w-5 tw:h-5" />
                  {{ $t('Go to payment page') }}
                </a>
              </Button>
              <Button variant="secondary" @click="displayQrcode(deposit.url)">
                <QrCodeIcon class="tw:w-5 tw:h-5" />
                {{ $t('Show QR Code') }}
              </Button>
              <Button variant="secondary" @click="onClickPayDeposit(deposit)">
                <CirclePlusIcon class="tw:w-5 tw:h-5" />
                {{ $t('Add payment') }}
              </Button>
            </div>
          </template>
        </div>
      </div>
      <div class="card-footer text-muted">
        <div class="d-flex justify-content-between align-items-end">
          <div>
            <ul class="list-unstyled text-muted mb-0 d-flex flex-column flex-md-row">
              <li>
                <small>
                  <i class="fa fa-calendar"></i>
                  {{ $t("Created at:") }} {{ dayjs(deposit.created_at).format('LLL') }}
                </small>
              </li>
              <li class="ml-md-2">
                <small>
                  <i class="fa fa-clock-o" aria-hidden="true"></i>
                  {{ $t("Valid until:") }} {{ deposit.capture_before ? dayjs(deposit.capture_before).format('LLL') : 'N/A' }}
                </small>
              </li>
            </ul>
          </div>
          <div>
            <a href v-if="deposit.status === 'pending'" class="text-danger" @click.prevent="onClickCancel(deposit)">
              {{ $t('Cancel') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Dialog :open="showQrcode" @update:open="(isOpen) => showQrcode = isOpen">
    <DialogScrollContent>
      <DialogHeader>
        <DialogTitle>
          {{ $t('Deposit QR Code') }}
        </DialogTitle>
      </DialogHeader>
      <Qrcode v-if="qrcodeValue" :value="qrcodeValue" />
    </DialogScrollContent>
  </Dialog>

  <CreatePayment
    :payable-type="'App\\Models\\Deposit'"
    :payable-id="paybleId"
    :modes="paymentModes"
    :status-list="paymentStatus"
    :show="showCreatePayment && !! depositToPay"
    :due-amount="amountToPay"
    :currency="currency"
    @close="onCloseCreatePayment">
  </CreatePayment>

</template>
